import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import type { UserRole } from '@/types/auth';

/** SSI staff roles used by admin surfaces (Admin RoleId=1 normalizes in canAccessRoute). */
const SSI_ONLY: UserRole[] = ['SSIEmployee', 'SSIAdministrator'];

/**
 * Eight live roles that reach Drawing Vault today (PRO-tier buildings via resolveScope)
 * plus the migration-only vault cohort. `Admin` normalizes to SSIAdministrator in
 * canAccessRoute. US-DVU-06 — do not drop User / LimitedUser / ReadOnly.
 */
const DRAWING_VAULT_ROLES: UserRole[] = [
  'SSIAdministrator',
  'SSIEmployee',
  'CompanyAdministrator',
  'CompanyManager',
  'User',
  'LimitedUser',
  'ReadOnly',
  'DrawingVaultUser',
];

/** D10 / US-DVU-06 — exactly five product surfaces for the vault-only role. */
const DRAWING_VAULT_USER_ROLE = 'DrawingVaultUser' as const;

/**
 * Unlisted paths DrawingVaultUser may still reach (landing shell, account, file viewers).
 * Everything else fails closed for this role (PRD §12.4).
 */
const DRAWING_VAULT_USER_EXTRA_ALLOWED = [
  '/',
  '/profile',
  '/account',
  '/pdf-viewer',
  '/dxf-viewer',
  '/image-viewer',
] as const;

/** Route-level permission matrix: which roles can access which features */
const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  // FR-644 — TruSpace API keys: the company administrator (and SSI administrators).
  '/account/api-keys': ['CompanyAdministrator', 'SSIAdministrator'],
  // FR-020/021/030/031 — property-system integration previews (sample data): same audience as API keys.
  '/account/integrations': ['CompanyAdministrator', 'SSIAdministrator'],
  // PRD §01 Dashboard §5.4 — Limited User has no Dashboard on any tier.
  // BASE-tier denial is enforced at the LandingRedirect level (it can't
  // be modeled here because tier is per-company, not per-role).
  '/dashboard': ['User', 'CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  '/properties': ['User', 'CompanyAdministrator', 'CompanyManager', 'LimitedUser', 'ReadOnly', 'SSIEmployee', 'SSIAdministrator'],
  // DEF-154: "Add Property" is a v3-only capability with no v2 equivalent
  // (company roles never create properties in v2 — that happens via a
  // third-party app). The create form must be SSI-only. This is a MORE
  // specific prefix than '/properties/' below, so canAccessRoute's
  // longest-prefix match makes it win for the exact '/properties/new' path
  // while '/properties/:id' + '/properties/:id/edit' keep the broader gate.
  '/properties/new': ['SSIEmployee', 'SSIAdministrator'],
  // Detail / edit paths under /properties — Limited User is list-only (D5 / RP-4-005).
  '/properties/': ['User', 'CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  // PRD 04-Reports §2 / AC-RPT-014: the Reports *hub* (list + tabs) is
  // CA/CM/SSI-only — User and LimitedUser stay off the nav surface.
  '/reports': ['CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  // DEF-411: Company User deep-links into measurement report viewer /
  // details / submit / SAB / compare from Properties (InternalReportsSection
  // → `/reports/:id/details`). Longest-prefix match makes `/reports/` win
  // over the hub gate for those paths without opening the hub itself.
  // LimitedUser / ReadOnly remain denied (BE annotate/finalize parity).
  '/reports/': [
    'User',
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
  ],
  // Hub child routes — keep CA/CM/SSI-only so `/reports/` does not leak
  // the measurement-list / review-queue surfaces to Company User.
  '/reports/measurement': [
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
  ],
  '/reports/reviews': [
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
  ],
  // Vault Reports is an SSI ops surface (portfolio-wide activity + company file
  // inventory). Not for company roles or the vault-only cohort — Drawing Vault
  // (file browser) stays on DRAWING_VAULT_ROLES; this reporting hub does not.
  '/drawings/reports': SSI_ONLY,
  // US-DVU-06: close the former default-open vault door; keep all eight live roles + cohort.
  '/drawing-vault': DRAWING_VAULT_ROLES,
  // Explicit Orders matrix (D6): all roles except Pending. Own-scope is
  // enforced server-side in orders.service.ts. US-DVU-06 adds DrawingVaultUser.
  '/orders': [
    'LimitedUser',
    'ReadOnly',
    'User',
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
    'DrawingVaultUser',
  ],
  // OD-2: LimitedUser/ReadOnly get scoped search (legacy RoleId=2).
  // FR-350: header Enter always opens search. Legacy `onSearchKeypress` in
  // myaccess.js goes to the search state with no role exception, and the
  // header input in Views/Home/Index.cshtml is not role-gated. DrawingVaultUser
  // must stay on that page instead of falling back to Drawing Vault.
  '/search': [
    'LimitedUser',
    'ReadOnly',
    'User',
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
    'DrawingVaultUser',
  ],
  '/alerts': ['User', 'CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  '/messages': ['User', 'CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  // D1 / DEF-199: Admin surface is SSI-only (CA/CM no longer see the Admin tab).
  '/admin': SSI_ONLY,
  '/admin/companies': SSI_ONLY,
  // DV admin (ADR-0019): the entitlement overlay is managed by SSI staff only.
  '/admin/drawing-vault': SSI_ONLY,
  // Internal roadmap + feature-request backlog (prefix covers /requests/:id).
  '/admin/roadmap': SSI_ONLY,
  '/admin/settings': SSI_ONLY,
  '/admin/audit-trail': SSI_ONLY,
  '/admin/pricing': SSI_ONLY,
  '/admin/domains': SSI_ONLY,
  '/admin/subscriptions': SSI_ONLY,
  '/admin/email-templates': SSI_ONLY,
  '/admin/company-flags': SSI_ONLY,
  '/admin/tooltips': SSI_ONLY,
  '/admin/subscription-limits': SSI_ONLY,
  '/admin/property-imports': SSI_ONLY,
  '/admin/widget-permissions': SSI_ONLY,
  // Legacy bookmark; route redirects to Teams. Kept for deep-link parity.
  '/admin/users': ['CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  // PRD 06-Teams §2: end users (User/LimitedUser) cannot see Teams.
  '/teams': ['CompanyAdministrator', 'CompanyManager', 'SSIEmployee', 'SSIAdministrator'],
  // PRD 09-Support §2: every role sees the Support menu. The admin
  // tabs inside the page are gated by isSSIRole() at the component level.
  '/support/hc': [
    'SSIAdministrator',
    'SSIEmployee',
    'CompanyAdministrator',
    'CompanyManager',
    'User',
    'LimitedUser',
    'ReadOnly',
    'DrawingVaultUser',
  ],
  '/support': [
    'LimitedUser',
    'ReadOnly',
    'User',
    'CompanyAdministrator',
    'CompanyManager',
    'SSIEmployee',
    'SSIAdministrator',
    'DrawingVaultUser',
  ],
};

function isDrawingVaultUserExtraAllowed(route: string): boolean {
  return DRAWING_VAULT_USER_EXTRA_ALLOWED.some(
    (prefix) => route === prefix || (prefix !== '/' && route.startsWith(`${prefix}/`)),
  );
}

/**
 * Returns whether `role` may navigate to `route` per ROUTE_PERMISSIONS.
 * Longest matching prefix wins.
 * - Most roles: unlisted routes are allowed (legacy default-open).
 * - `DrawingVaultUser`: unlisted routes are denied (fail closed — PRD §12.4),
 *   except landing `/`, account surfaces, and drawing viewers.
 * RoleId=1 `Admin` is treated as SSIAdministrator for matrix lookup (D3).
 */
export function canAccessRoute(role: string, route: string): boolean {
  const r = role === 'Admin' ? 'SSIAdministrator' : role;

  // Find the most specific matching route
  const sortedRoutes = Object.keys(ROUTE_PERMISSIONS)
    .filter((prefix) => route.startsWith(prefix))
    .sort((a, b) => b.length - a.length);

  const match = sortedRoutes[0];

  if (r === DRAWING_VAULT_USER_ROLE) {
    if (match) {
      return ROUTE_PERMISSIONS[match].includes(DRAWING_VAULT_USER_ROLE);
    }
    return isDrawingVaultUserExtraAllowed(route);
  }

  if (!match) return true; // No restriction defined = allow

  return ROUTE_PERMISSIONS[match].includes(r as UserRole);
}

/**
 * The first route a role can actually reach when it's been denied its
 * requested page. Shared by `ProtectedRoute` (the top-level router guard)
 * and every SSI-only page's own defense-in-depth guard (DEF-130) so a
 * no-access role always lands on a real page instead of a route it also
 * can't access — which stalls the page in a redirect loop that renders
 * blank until the loop resolves.
 *
 * Never `/` (that's `LandingRedirect`, which can re-dispatch back to a
 * denied route and loop) and never a hardcoded `/dashboard` — LimitedUser
 * and ReadOnly have no Dashboard access on any tier (PRD §01 Dashboard
 * §5.4), so that target 404-loops for them specifically.
 */
export function fallbackPathForRole(role: string): string {
  // US-DVU-05: the vault-only cohort has no Properties access, so it always
  // lands on Drawing Vault rather than walking the shared candidate list.
  if (role === DRAWING_VAULT_USER_ROLE) {
    return '/drawing-vault';
  }
  const candidates = isReleaseFeatureEnabled('support-zendesk')
    ? ['/properties', '/orders', '/alerts']
    : ['/properties', '/support', '/orders', '/alerts'];
  const ultimate = isReleaseFeatureEnabled('support-zendesk') ? '/properties' : '/support';
  return candidates.find((path) => canAccessRoute(role, path)) ?? ultimate;
}

export function isSSIRole(role: string): boolean {
  return role === 'SSIAdministrator' || role === 'SSIEmployee' || role === 'Admin';
}

/**
 * Legacy RoleId=2 — vault-only portfolio access (DEF-37 / myaccess.js:2340-2362).
 * `ReadOnly` is the JWT/backfill alias for the same Limited User role.
 */
export function isLimitedUser(role: string): boolean {
  return role === 'LimitedUser' || role === 'ReadOnly';
}

/**
 * Roles that see the Admin section in the sidebar.
 * D1 / DEF-199: SSI-only — Company Admin / Manager must not see the Admin nav
 * (routes remain gated separately via canAccessRoute).
 */
export function isAdminRole(role: string): boolean {
  return isSSIRole(role);
}
